import React, { useEffect, useState } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Badge, CustomModal, Loading, SectionTitle } from '../../../../components'
import * as colors from '../../../../assets/js/colors'
import { getCiot } from '../../../../services/api/https/deliveries/ciot.http'
import {
  BadgeStatusCiot,
  CIOT_LOTACAO,
  TIPOS_CARGA_CIOT,
  estiloAvisoCiot,
  formatarDataHora,
  formatarDataIso,
  formatarDocumento,
  formatarMoeda,
  rotuloCiot,
} from './ciot.util'

const NOMES_EVENTO = {
  gerar: 'Geração do número',
  declarar: 'Declaração',
  consultar: 'Consulta',
  encerrar: 'Encerramento',
  cancelar: 'Cancelamento',
  retificar: 'Retificação',
}

const estiloRotulo = { fontSize: '0.8rem', color: '#14B0BF', display: 'block' }

function Campo({ rotulo, children }) {
  return (
    <div className="p-md-3 p-col-6">
      <span style={estiloRotulo}>{rotulo}</span>
      <span style={{ fontSize: '0.9rem' }}>{children || '-'}</span>
    </div>
  )
}

export default function ModalDetalhesCiot({ visible, ciotId, onClose, onError }) {
  const [ciot, setCiot] = useState(null)

  useEffect(() => {
    if (!visible || !ciotId) return
    let ativo = true
    setCiot(null)
    getCiot(ciotId)
      .then(detalhe => ativo && setCiot(detalhe))
      .catch(error => {
        if (!ativo) return
        onError(error)
        onClose()
      })
    return () => {
      ativo = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, ciotId])

  const dados = ciot && ciot.dados && typeof ciot.dados === 'object' ? ciot.dados : null
  const tipoCarga = dados
    ? TIPOS_CARGA_CIOT.find(item => item.value === Number(dados.codigo_tipo_carga))
    : null

  return (
    <CustomModal
      isVisible={visible}
      header={ciot ? `CIOT ${rotuloCiot(ciot)}` : 'CIOT'}
      style={{ width: '90vw', maxWidth: 950 }}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
      onClose={onClose}
    >
      {!ciot ? (
        <div className="text-center" style={{ marginTop: 20, marginBottom: 20 }}>
          <Loading />
        </div>
      ) : (
        <div className="p-grid">
          <Campo rotulo="Status">
            <BadgeStatusCiot status={ciot.status} />
          </Campo>
          <Campo rotulo="Tipo de operação">
            {Number(ciot.tipo_operacao) === CIOT_LOTACAO ? 'Carga lotação' : 'Carga fracionada'}
          </Campo>
          <Campo rotulo="Protocolo ANTT">{ciot.protocolo}</Campo>
          <Campo rotulo="Placa">{ciot.placa}</Campo>
          <Campo rotulo="Valor do frete">{formatarMoeda(ciot.valor_frete)}</Campo>
          <Campo rotulo="Peso da carga (kg)">
            {ciot.peso_carga ? Number(ciot.peso_carga).toLocaleString('pt-BR') : null}
          </Campo>
          <Campo rotulo="Viagem">
            {ciot.data_inicio_viagem
              ? `${formatarDataIso(ciot.data_inicio_viagem)} a ${formatarDataIso(
                  ciot.data_fim_viagem,
                )}`
              : null}
          </Campo>
          <Campo rotulo="Gerado em">{formatarDataHora(ciot.created_at)}</Campo>
          <Campo rotulo="Declarado em">{formatarDataHora(ciot.declarado_em)}</Campo>
          <Campo rotulo="Encerrado em">{formatarDataHora(ciot.encerrado_em)}</Campo>
          <Campo rotulo="Cancelado em">{formatarDataHora(ciot.cancelado_em)}</Campo>
          <Campo rotulo="Motivo do cancelamento">{ciot.motivo_cancelamento}</Campo>

          {ciot.mensagem && (
            <div className="p-col-12">
              <div style={{ ...estiloAvisoCiot, whiteSpace: 'pre-line' }}>{ciot.mensagem}</div>
            </div>
          )}
          {ciot.aviso_transportador && (
            <div className="p-col-12">
              <div style={estiloAvisoCiot}>
                <strong>Aviso da ANTT ao transportador:</strong> {ciot.aviso_transportador}
              </div>
            </div>
          )}

          {dados && (
            <>
              <div className="p-col-12" style={{ marginTop: 10 }}>
                <SectionTitle title="Dados declarados" />
              </div>
              <Campo rotulo="Contratado">{formatarDocumento(dados.cpf_cnpj_contratado)}</Campo>
              <Campo rotulo="Contratante">{formatarDocumento(dados.cpf_cnpj_contratante)}</Campo>
              <Campo rotulo="Destinatário">{formatarDocumento(dados.cpf_cnpj_destinatario)}</Campo>
              <Campo rotulo="Natureza / tipo de carga">
                {`${dados.codigo_natureza_carga || '-'} / ${tipoCarga ? tipoCarga.label : '-'}`}
              </Campo>
              {(dados.contratantes_carga_fracionada || []).length > 0 && (
                <div className="p-col-12">
                  <span style={estiloRotulo}>Demais contratantes</span>
                  <span style={{ fontSize: '0.9rem' }}>
                    {dados.contratantes_carga_fracionada.map(formatarDocumento).join(', ')}
                  </span>
                </div>
              )}
              <div className="p-col-12">
                <span style={estiloRotulo}>Veículos</span>
                <span style={{ fontSize: '0.9rem' }}>
                  {(dados.veiculos || [])
                    .map(veiculo => `${veiculo.placa} (${veiculo.eixos} eixos)`)
                    .join(', ')}
                </span>
              </div>
              <div className="p-col-12">
                <span style={estiloRotulo}>Trechos</span>
                <span style={{ fontSize: '0.9rem' }}>
                  {(dados.origens_destinos || [])
                    .map(
                      trecho =>
                        `${trecho.nome_municipio_origem || trecho.codigo_municipio_origem} → ${
                          trecho.nome_municipio_destino || trecho.codigo_municipio_destino
                        } (${trecho.distancia_km} km)`,
                    )
                    .join('; ')}
                </span>
              </div>
            </>
          )}

          <div className="p-col-12" style={{ marginTop: 10 }}>
            <SectionTitle title="Histórico na ANTT" />
          </div>
          <div className="p-col-12">
            <DataTable value={ciot.eventos || []} emptyMessage="Sem eventos">
              <Column
                header="Data"
                style={{ width: 140 }}
                body={evento => formatarDataHora(evento.created_at)}
              />
              <Column
                header="Evento"
                style={{ width: 150 }}
                body={evento => NOMES_EVENTO[evento.tipo] || evento.tipo}
              />
              <Column
                header="Resultado"
                style={{ width: 100, textAlign: 'center' }}
                body={evento =>
                  evento.sucesso ? (
                    <Badge color={colors.verde_success}>Sucesso</Badge>
                  ) : (
                    <Badge color={colors.vermelho_hover}>Falha</Badge>
                  )
                }
              />
              <Column field="usuario" header="Usuário" style={{ width: 150 }} />
              <Column
                header="Mensagem"
                body={evento => (
                  <span style={{ fontSize: '0.8rem', whiteSpace: 'pre-line' }}>
                    {evento.mensagem}
                  </span>
                )}
              />
            </DataTable>
          </div>
        </div>
      )}
    </CustomModal>
  )
}
